import { examMetadataModule } from '@/store/examMetadata/module'
import { quizModule } from '@/store/quiz/module'
import axios, { AxiosError } from 'axios'
import { tutorModule } from '@/store/tutor/module'
import { blockNames, type BlockNames } from '@/store/tutor/types'
import { type TTutorState } from '@/store/tutor/state'
import { analyticsModule } from '@/store/analytics/module'
import { getNumericQuizMode } from '@/utils'
import * as Sentry from '@sentry/browser'
import { userModule } from '@/store/user/module'

/**
 * Toggle tutor chat window open/close
 */
const toggleTutorChat = (currentQuestionSerial?: string, currentExamObjId?: string) => {
    localStorage.setItem('hasInteractedWithTutorChat', 'true')
    tutorModule.state.isChatOpen = !tutorModule.state.isChatOpen
    tutorModule.state.hasInteractedWithTutorChat = true
    if (tutorModule.state.isChatOpen && !tutorModule.state.hasOpenedChatDuringQuiz) {
        const currentQuestionIndex = quizModule.getters.getActiveQuiz()?.currentQuestionIndex
        tutorModule.state.hasOpenedChatDuringQuiz = true
        tutorModule.state.greetingQuestionIndex = currentQuestionIndex ?? null
    }

    const chatUuid = tutorModule.getters.getChatUuid(currentQuestionSerial, currentExamObjId)
    analyticsModule.actions.amplitudeTrack(
        'Tutor Chat Toggled',
        {
            tutorChatToggle: tutorModule.state.isChatOpen ? 'open' : 'close',
            chatUuid: chatUuid || '',
        }
    )
}

/**
 * Given a request from the user, generate a streaming tutor response
 */
const sendTextbookSearchRequest = async (request: string, primingMessage: string | null) => {
    const sessionToken = userModule.getters.getSessionToken()
    if (!sessionToken) {
        return textbookRequestError('You must be logged in to use the Tutor AI.')
    }

    // setup request config
    const exam = examMetadataModule.getters.getCurrentExamMetadata()
    const quiz = quizModule.getters.getActiveQuiz()
    const question = quiz?.questions[quiz.currentQuestionIndex]
    const userAnswers = quiz?.answers.find(a => a.questionSerial === question?.serial)

    // throw if missing anything essential
    if (!exam) {
        return textbookRequestError('No current exam found.')
    }
    if (!question) {
        return textbookRequestError('No active question found.')
    }

    // start stream
    tutorModule.state.textbookSearch.isLoading = true
    const response = await axios.post(
        `${import.meta.env.VUE_APP_POCKETBRAIN_URL}/search-textbooks`,
        {
            examMetadataId: exam.objectId,
            questionSerial: question.serial,
            selectedChoices: userAnswers?.selectedChoices || [],
            request,
            ...(primingMessage && { primingMessage: primingMessage }),
            startedAtISO: quiz.startedAt,
        },
        {
            headers: {
                'session-token': sessionToken,
                'Content-Type': 'application/json',
            },
            'axios-retry': { retries: 0 },
        }
    ).catch((err: unknown) => {
        if (err instanceof AxiosError) {
            tutorModule.state.textbookSearch.error = err.response?.data?.error
            tutorModule.state.textbookSearch.isLoading = false
        }
    })

    if (!response?.data) {
        return textbookRequestError('Unable to complete request.')
    }

    const parsedResponseBody: {
        references: TTutorState['textbookSearch']['results']
        cost: {
            totalCost: number
            embeddingCost: number
            generationCost: number
        }
    } = response.data

    tutorModule.state.textbookSearch.results = parsedResponseBody.references
    tutorModule.state.cost = (tutorModule.state.cost || 0) + (parsedResponseBody.cost.totalCost || 0)
    tutorModule.state.textbookSearch.isLoading = false
}

/**
 * Reset tutor session state
 */
const resetTutorState = () => {
    tutorModule.state.chat = []
    tutorModule.state.textbookSearch = { isLoading: false, results: [] }
    tutorModule.state.cost = 0
    tutorModule.state.isTutorResponding = false
    tutorModule.state.isLoadingSessionHistory = false
    tutorModule.state.nudgePrimingMessageType = null
    tutorModule.state.rating = 0
}

/**
 * If the tutor request errors out or has to end early, clean up the stream and show error message
 */
const tutorRequestError = (error: string) => {
    tutorModule.state.isTutorResponding = false
    tutorModule.state.isLoadingSessionHistory = false
    tutorModule.state.chat = tutorModule.state.chat.filter(m => m.role !== 'system')
    const lastMessage = tutorModule.state.chat[tutorModule.state.chat.length - 1]
    if (lastMessage) {
        lastMessage.isStreaming = false
    }
    tutorModule.state.chat.push({
        role: 'system',
        content: `Error: ${error}`,
    })

    return error
}

/**
 * If the textbook search request errors out or has to end early, clean up the stream and show error message
 */
const textbookRequestError = (error: string) => {
    return error
}

/**
 * Fetch tutor session chat logs based on the current question
 */
const fetchTutorSession = async () => {
    resetTutorState()

    const pocketbrainUrlEnvVariable = import.meta.env.VUE_APP_POCKETBRAIN_URL
    const tutorUrl = `${pocketbrainUrlEnvVariable}/fetch-tutor-session`
    if (!pocketbrainUrlEnvVariable) {
        return tutorRequestError('Tutor service URL not configured.')
    }

    const sessionToken = userModule.getters.getSessionToken()
    if (!sessionToken) {
        return tutorRequestError('You must be logged in to use the Tutor AI.')
    }

    const exam = examMetadataModule.getters.getCurrentExamMetadata()
    const quiz = quizModule.getters.getActiveQuiz()
    const question = quiz?.questions[quiz.currentQuestionIndex]

    if (!exam) {
        return tutorRequestError('No current exam found.')
    }

    tutorModule.state.isLoadingSessionHistory = true
    const tutorSession = await axios.post(
        tutorUrl,
        {
            examMetadataId: exam.objectId,
            questionSerial: question?.serial,
        },
        {
            headers: {
                'session-token': sessionToken,
                'Content-Type': 'application/json',
            },
        }
    ).catch((e: unknown) => {
        tutorRequestError('Unable to load previous chat session.')
        throw e
    })

    const { chat, cost, references, rating } = (tutorSession?.data || {}) as {
        cost: number
        chat: TTutorState['chat']
        references: TTutorState['textbookSearch']['results']
        rating?: number
    }
    tutorModule.state.isLoadingSessionHistory = false
    tutorModule.state.chat = chat
    tutorModule.state.cost = cost
    tutorModule.state.textbookSearch.results = references || []
    tutorModule.state.rating = rating || 0
    return chat
}

/**
 * Given a request from the user, generate a streaming tutor response
 */
const sendTutorRequest = async (param: {
    request: string
    primingMessage: string | null
    choiceSeed: number[] | null
}) => {
    const { request, primingMessage, choiceSeed } = param
    tutorModule.state.isTutorResponding = true

    const sessionToken = userModule.getters.getSessionToken()
    if (!sessionToken) {
        return tutorRequestError('You must be logged in to use the Tutor AI.')
    }

    // fetch context
    const exam = examMetadataModule.getters.getCurrentExamMetadata()
    const quiz = quizModule.getters.getActiveQuiz()
    const question = quiz?.questions[quiz.currentQuestionIndex]

    // kick off textbook reference search if we don't already have references
    // textbook reference search should be kicked off at the beginning of the chat history
    // kicking it off at the beginning of the chat avoids us recalling in the case of an error or no scanned textbooks
    // textbook search does NOT happen if user is not in an active quiz
    if (
        !tutorModule.state.textbookSearch.isLoading
        && !tutorModule.state.textbookSearch.results.length
        && !tutorModule.state.chat.length
        && quiz
    ) {
        sendTextbookSearchRequest(request, primingMessage)
    }

    // If a priming message is sent and it is the chat.length = 0
    // add the priming message to the chat state so the UI can reflect the
    // message until pocketBrain returns and the session is updated
    if (primingMessage && !tutorModule.state.chat.length) {
        tutorModule.state.chat.push({
            role: 'assistant',
            content: primingMessage,
        })
    }

    // log request to chat
    tutorModule.state.chat.push({
        role: 'user',
        content: request,
    })

    // setup request config
    const context = quiz
        ? 'The student is currently taking a practice quiz.'
        : `The student is currently studying for the ${exam?.nativeAppName} exam.`
    const userAnswers = quiz?.answers.find(a => a.questionSerial === question?.serial)

    // throw if missing anything essential
    if (!exam) {
        return tutorRequestError('No current exam found.')
    }

    // Show a thinking message after a short delay so users know their request was sent
    setTimeout(() => {
        if (tutorModule.state.isTutorResponding) {
            tutorModule.state.chat.push({
                role: 'system',
                content: 'Thinking...',
            })
        }
    }, 300)

    if (tutorModule.state.chat.length === 0) {
        Sentry.captureException(new Error('SendTutorRequest: Chat empty before axios call'), {
            extra: {
                tutorState: tutorModule.state.chat,
                userRequest: request,
            },
        })
    }

    // start stream
    const response = await fetch(`${import.meta.env.VUE_APP_POCKETBRAIN_URL}/tutor`, {
        method: 'POST',
        body: JSON.stringify({
            context,
            examMetadataId: exam.objectId,
            questionSerial: question?.serial,
            selectedChoices: userAnswers?.selectedChoices || [],
            request,
            ...(primingMessage && { primingMessage: primingMessage }),
            quizMode: quiz && Number(getNumericQuizMode(quiz.mode)),
            startedAtISO: quiz?.startedAt,
            choiceSeed,
            useTools: true,
        }),
        headers: {
            'session-token': sessionToken,
            'Content-Type': 'application/json',
        },
    }).catch((e: unknown) => {
        tutorRequestError('Unable to complete request.')
        throw e
    })

    if (!response.body) {
        return tutorRequestError('Unable to complete request.')
    }

    if (!response.ok) {
        const errorResponse = await response.json() as { error: string; code: string }
        return tutorRequestError(errorResponse.error)
    }

    const stream = response.body.getReader()
    const decoder = new TextDecoder()
    let streamDone = false

    // prepare object to store data as it is streamed
    const collectedData: {
        type: BlockNames
        content: string
    }[] = []
    let currentBlock: BlockNames | null = null
    if (tutorModule.state.chat.length === 0) {
        Sentry.captureException(new Error('SendTutorRequest: Chat empty before Do block'), {
            extra: {
                tutorChat: tutorModule.state.chat,
                userRequest: request,
            },
        })
    }

    do {
        const { value, done } = await stream.read().catch((e: unknown) => {
            tutorRequestError('Unable to complete request.')
            throw e
        })
        streamDone = done

        if (!value) {
            continue
        }

        // decode and cleanup chunks
        const decodedChunk = decoder.decode(value, { stream: true })
        const chunks = decodedChunk.split('data: ')
        const cleanedChunks = chunks.reduce((acc, chunk) => {
            const cleanedChunk = chunk.replace(/\n\n$/g, '')
            if (cleanedChunk) {
                acc.push(cleanedChunk)
            }
            return acc
        }, Array<string>())

        // iterate over all chunks
        for (const c of cleanedChunks) {
            // if is shortcircuit block, process shortcircuit message and end stream
            if (c.startsWith('<shortcircuit>') && c.endsWith('</shortcircuit>')) {
                const shortcircuitMessage = c.slice(14, c.length - 15)
                const lastBlock = tutorModule.state.chat[tutorModule.state.chat.length - 1]
                if (lastBlock.role === 'system') {
                    lastBlock.content = shortcircuitMessage
                } else {
                    tutorModule.state.chat.push({
                        role: 'system',
                        content: shortcircuitMessage,
                    })
                }
                streamDone = true

                break
            }

            // if chunk is a block delimiter, update current block
            if (c.startsWith('%%') && c.endsWith('%%')) {
                const blockName = c
                    .replace('%%START_', '')
                    .replace('%%END_', '')
                    .replace('%%', '') as BlockNames

                if (blockNames.includes(blockName)) {
                    currentBlock = blockName

                    if (c.startsWith('%%START_')) {
                        collectedData.push({
                            type: blockName,
                            content: '',
                        })

                        if (blockName === 'TEXT_BLOCK') {
                            tutorModule.state.chat = tutorModule.state.chat.filter(m => m.role !== 'system')
                            tutorModule.state.chat.push({
                                role: 'assistant',
                                content: '',
                                isStreaming: true,
                            })
                        }
                        if (blockName === 'TOOL_USE_BLOCK') {
                            tutorModule.state.chat.push({
                                role: 'tool_use',
                                content: '',
                                isStreaming: true,
                            })
                        }
                        if (blockName === 'TOOL_RESULT_BLOCK') {
                            tutorModule.state.chat.push({
                                role: 'tool_use',
                                content: '',
                                isStreaming: true,
                            })
                        }
                    } else if (c.startsWith('%%END_')) {
                        const lastBlock = tutorModule.state.chat[tutorModule.state.chat.length - 1]
                        if (lastBlock) {
                            lastBlock.isStreaming = false
                        }
                        currentBlock = null
                    }
                } else {
                    currentBlock = null
                }
            } else if (currentBlock) {
                // otherwise, store chunk data in current block
                collectedData[collectedData.length - 1].content += c

                // add delta onto last chat message in store if it's one we show and last block is
                // assistant chat message and in loading state
                const lastBlock = tutorModule.state.chat[tutorModule.state.chat.length - 1]
                if (
                    currentBlock === 'TEXT_BLOCK'
                    && lastBlock?.isStreaming
                    && lastBlock?.role === 'assistant'
                ) {
                    lastBlock.content += c
                }
                if (currentBlock === 'TOOL_RESULT_BLOCK' || currentBlock === 'TOOL_USE_BLOCK') {
                    lastBlock.content = c
                }
            }
        }
    } while (!streamDone)

    const metadataJSON = collectedData.find(d => d.type === 'METADATA')?.content
    const metadata = JSON.parse(metadataJSON || '{}') as {
        totalCost?: number
        embeddingCost?: number
        generationCost?: number
    }
    tutorModule.state.cost = (tutorModule.state.cost || 0) + (metadata.totalCost || 0)
    tutorModule.state.isTutorResponding = false
    tutorModule.state.hasSentTutorMessageDuringQuiz = true
}

/**
 * If the rate session request errors out or has to end early, clean up the stream and show error message
 */
const rateTutorSessionError = (error: string) => {
    return error
}

const rateTutorSession = async (rating: -1 | 0 | 1) => {
    const sessionToken = userModule.getters.getSessionToken()
    if (!sessionToken) {
        return rateTutorSessionError('You must be logged in to use the Tutor AI.')
    }

    // setup request config
    const exam = examMetadataModule.getters.getCurrentExamMetadata()
    const quiz = quizModule.getters.getActiveQuiz()
    const question = quiz?.questions[quiz.currentQuestionIndex]

    // throw if missing anything essential
    if (!exam) {
        return rateTutorSessionError('No current exam found.')
    }
    if (!question?.serial) {
        return rateTutorSessionError('No active question found.')
    }

    await axios.post(
        `${import.meta.env.VUE_APP_POCKETBRAIN_URL}/rate-session`,
        {
            examMetadataId: exam.objectId,
            questionSerial: question.serial,
            rating,
        },
        {
            headers: {
                'session-token': sessionToken,
                'Content-Type': 'application/json',
            },
        }
    ).catch((e: unknown) => {
        rateTutorSessionError('Unable to rate tutor session.')
        throw e
    })

    tutorModule.state.rating = rating
}

/**
 * Clear out existing session to start a new one
 */
const startNewSession = async () => {
    const exam = examMetadataModule.getters.getCurrentExamMetadata()
    const quiz = quizModule.getters.getActiveQuiz()
    const question = quiz?.questions[quiz.currentQuestionIndex]
    const sessionToken = userModule.getters.getSessionToken()
    if (!sessionToken) {
        return rateTutorSessionError('You must be logged in to use the Tutor AI.')
    }

    await axios.post(
        `${import.meta.env.VUE_APP_POCKETBRAIN_URL}/start-new-session`,
        {
            examMetadataId: exam?.objectId,
            questionSerial: question?.serial,
        },
        {
            headers: {
                'session-token': sessionToken,
                'Content-Type': 'application/json',
            },
        }
    ).catch((e: unknown) => {
        rateTutorSessionError('Unable to start a new tutor session.')
        throw e
    })

    resetTutorState()
}

/**
 * Fetch and store tutor name from experiment
 */
const fetchTutorNameTestVariantLabel = async () => {
    // if there is any label in state, just immediately return it
    if (tutorModule.state.tutorNameTestVariantLabel) {
        return tutorModule.state.tutorNameTestVariantLabel
    }

    // otherwise, fetch the experiment from amplitude and try to use the payload label value
    try {
        const tutorNameTestVariant = await analyticsModule.actions.amplitudeFetchVariant('tutor-name-test')
        if (tutorNameTestVariant?.key !== 'off' && tutorNameTestVariant?.payload?.label) {
            tutorModule.state.tutorNameTestVariantLabel = tutorNameTestVariant?.payload?.label
        } else {
            // if the experiment is off, doesn't exist, or the label is missing, default to "Tutor"
            tutorModule.state.tutorNameTestVariantLabel = 'Tutor'
        }
    } catch {
        tutorModule.state.tutorNameTestVariantLabel = 'Tutor'
    }

    return tutorModule.state.tutorNameTestVariantLabel
}

export default {
    sendTutorRequest,
    sendTextbookSearchRequest,
    fetchTutorSession,
    toggleTutorChat,
    rateTutorSession,
    startNewSession,
    fetchTutorNameTestVariantLabel,
}
